// src/components/PayBill.js
import React, { useState, useEffect } from "react";
import api from "../Service/api";
import "./PayBill.css"; // Use same CSS as CategoryForm

const PayBill = () => {
  const [vendorList, setVendorList] = useState([]);
  const [vendorName, setVendorName] = useState("");
  const [amountPaid, setAmountPaid] = useState("");
  const [description, setDescription] = useState("");
  const [vendorInvoiceNumber, setVendorInvoiceNumber] = useState(""); // Will store like "V-1"
  const [paymentDate, setPaymentDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [loading, setLoading] = useState(false);
  const [vendorBalance, setVendorBalance] = useState(null);

  // Fetch next invoice number on component mount
  useEffect(() => {
    fetchNextInvoiceNumber();
  }, []);

  const fetchNextInvoiceNumber = async () => {
    try {
      const res = await api.get("/billPayment/vendor/next-invoice");
      
      console.log("API Response for next invoice:", res.data);
      
      // Response is { "nextInvoice": 1 }
      if (res.data && res.data.nextInvoice !== undefined) {
        const nextNumber = res.data.nextInvoice;
        const newInvoiceNumber = `V-${nextNumber}`;
        console.log("Setting invoice number to:", newInvoiceNumber);
        setVendorInvoiceNumber(newInvoiceNumber); // Add V- prefix
      } else {
        // If API fails, default to V-1
        console.log("API response invalid, defaulting to V-1");
        setVendorInvoiceNumber('V-1');
      }
    } catch (err) {
      console.error("Error fetching next invoice number:", err);
      // Default to V-1 if error
      setVendorInvoiceNumber('V-1');
    }
  };

  useEffect(() => {
    if (!vendorName) {
      setVendorBalance(null);
      return;
    }

    const fetchVendorBalance = async () => {
      try {
        const res = await api.get(
          `/billPayment/vendor-balance/${vendorName}`
        );

        if (res.data.code === 1) {
          setVendorBalance(res.data.additionalDetail);
        } else {
          setVendorBalance(null);
        }
      } catch (err) {
        console.error("Error fetching balance:", err);
        setVendorBalance(null);
      }
    };

    fetchVendorBalance();
  }, [vendorName]);

  // Fetch vendors on mount
  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await api.get("/product/getAll");
        if (res.data.code === 1 && res.data.additionalDetail) {
          setVendorList(res.data.additionalDetail);
        }
      } catch (err) {
        console.error("Error fetching vendors:", err);
      }
    };
    fetchVendors();
  }, []);

  useEffect(() => {
  const handleWheel = (e) => {
    if (e.target.type === 'number') {
      e.preventDefault();
    }
  };
  document.addEventListener('wheel', handleWheel, { passive: false });
  return () => document.removeEventListener('wheel', handleWheel);
}, []);

  const handleSubmit = async () => {
    if (!vendorName || !amountPaid) {
      alert("Please select a vendor and enter the amount.");
      return;
    }

    if (!vendorInvoiceNumber) {
      alert("Invoice number is being generated. Please wait...");
      return;
    }

    setLoading(true);

    try {
      console.log("Sending payload:", {
        vendorName,
        amountPaid: parseFloat(amountPaid),
        description: description,
        paymentDate: paymentDate,
        vendorInvoiceNumber: vendorInvoiceNumber // Send full invoice number with V- prefix
      });

      const res = await api.post("/billPayment/company_pay_bill", {
        vendorName,
        amountPaid: parseFloat(amountPaid),
        description: description,
        paymentDate: paymentDate,
        vendorInvoiceNumber: vendorInvoiceNumber 
      });

      if (res.data.code === 1 || res.data.success) {
        alert(
          `Payment Successful! Invoice: ${vendorInvoiceNumber}\n` +
          (res.data.additionalDetail || `Remaining Balance: ${res.data.remainingBalance}`)
        );
        
        // IMPORTANT: Wait for the next invoice number to be fetched
        // before resetting the form
        await fetchNextInvoiceNumber();
        
        // Reset form but keep the new invoice number
        setVendorName("");
        setAmountPaid("");
        setDescription("");
        setPaymentDate(new Date().toISOString().split('T')[0]); // Reset to today
      } else {
        alert(res.data.additionalDetail || res.data.message || "Payment failed");
      }
    } catch (err) {
      const msg =
        err.response?.data?.additionalDetail ||
        err.response?.data?.message ||
        "Something went wrong";
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="category-form-container">
      <h2>💳 Pay Vendor Bill</h2>

      <div className="category-table">
        {/* Vendor Name */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Vendor Name</label>
          <input
            type="text"
            list="vendorNames"
            value={vendorName}
            onChange={(e) => setVendorName(e.target.value)}
            placeholder="Enter Vendor Name"
            className="category-input"
          />
          {vendorBalance !== null && (
            <table className="category-table" style={{ marginBottom: "15px" }}>
              <thead>
                <tr>
                  <th>Vendor Name</th>
                  <th>Outstanding Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{vendorName}</td>
                  <td
                    style={{
                      fontWeight: "bold",
                      color: vendorBalance < 0 ? "#dc2626" : "#16a34a"
                    }}
                  >
                    {vendorBalance}
                  </td>
                </tr>
              </tbody>
            </table>
          )}

          <datalist id="vendorNames">
            {vendorList.map((v, i) => (
              <option key={i} value={v.vendorName} />
            ))}
          </datalist>
        </div>

        {/* Invoice Number Display (Read-only) */}
        {vendorInvoiceNumber && (
          <div style={{ marginBottom: "15px" }}>
            <label style={{ fontWeight: "bold" }}>Payment Number</label>
            <input
              type="text"
              value={vendorInvoiceNumber}
              readOnly
              className="category-input"
              style={{ 
                backgroundColor: "#f0f0f0",
                color: "#16a34a",
                fontWeight: "bold",
                border: "1px solid #16a34a"
              }}
            />
          </div>
        )}

        {/* Amount Paid */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Pay Bill</label>
          <input
            type="number"
            value={amountPaid}
            onChange={(e) => setAmountPaid(e.target.value)}
            placeholder="Enter amount"
            className="category-input"
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter description"
            className="category-input"
          />
        </div>

        {/* Payment Date */}
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontWeight: "bold" }}>Payment Date</label>
          <input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            className="category-input"
            max={new Date().toISOString().split('T')[0]}
          />
        </div>

        {/* Submit Button */}
        <button
          className="save-btn"
          onClick={handleSubmit}
          disabled={loading || !vendorInvoiceNumber}
        >
          {loading ? "Processing..." : "Pay Bill"}
        </button>
      </div>
    </div>
  );
};

export default PayBill;